import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Tag } from 'lucide-react';
import { vinApi } from '../../services/api';
import api from '../../services/api';
import { InspectionTagBadge } from '../inspection/InspectionTagBadge';
import { QuickTagInput } from '../inspection/QuickTagInput';
import { formatInspectionDateShort } from '../../utils/timezone';

interface InspectionDetailsModalProps {
  inspection: any | null;
  onClose: () => void;
  dotNumber?: number;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/api` : '/api';

export const InspectionDetailsModal: React.FC<InspectionDetailsModalProps> = ({ inspection, onClose, dotNumber }) => {
  const [violations, setViolations] = useState<any[]>([]);
  const [loadingViolations, setLoadingViolations] = useState(false);
  const [inspectionTags, setInspectionTags] = useState<string[]>([]);
  const [availableShipperTags, setAvailableShipperTags] = useState<Array<{ tagName: string; color: string }>>([]);
  const [showTagInput, setShowTagInput] = useState(false);

  useEffect(() => {
    if (inspection?.inspectionId || inspection?.inspection_id) {
      const inspectionId = inspection.inspectionId || inspection.inspection_id;
      setLoadingViolations(true);

      // Set timeout for slow API
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      vinApi.getInspectionViolations(inspectionId)
        .then(data => {
          clearTimeout(timeoutId);
          setViolations(data || []);
        })
        .catch(err => {
          clearTimeout(timeoutId);
          console.error('Error loading violations:', err);
          setViolations([]);
        })
        .finally(() => {
          setLoadingViolations(false);
        });

      // Fetch tags for this inspection
      fetchInspectionTags(inspectionId);
      fetchAvailableShipperTags();
    } else {
      setViolations([]);
      setLoadingViolations(false);
    }
  }, [inspection]);

  const fetchInspectionTags = async (inspectionId: string) => {
    try {
      const response = await api.get(`/inspection-tags/${inspectionId}`);
      const data = response.data;
      if (data.success) {
        setInspectionTags(data.data.map((t: any) => t.tagName));
      }
    } catch (error) {
      console.error('Failed to fetch inspection tags:', error);
    }
  };

  const fetchAvailableShipperTags = async () => {
    try {
      const response = await api.get('/inspection-tags/shipper-tags/available');
      const data = response.data;
      if (data.success) {
        setAvailableShipperTags(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch available tags:', error);
    }
  };

  const handleAddTag = async (tagName: string) => {
    const inspectionId = inspection.inspectionId || inspection.inspection_id;
    try {
      const response = await api.post('/inspection-tags', {
        inspectionId,
        dotNumber: dotNumber || inspection.dotNumber || inspection.dot_number,
        tagName
      });
      const data = response.data;
      if (data.success) {
        await fetchInspectionTags(inspectionId);
      }
    } catch (error) {
      console.error('Failed to add tag:', error);
    }
  };

  const handleRemoveTag = async (tagName: string) => {
    const inspectionId = inspection.inspectionId || inspection.inspection_id;
    try {
      await api.delete(`/inspection-tags/${inspectionId}/${encodeURIComponent(tagName)}`);
      const data = { success: true };
      if (data.success) {
        await fetchInspectionTags(inspectionId);
      }
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  };

  const getTagColor = (tagName: string): string => {
    const tag = availableShipperTags.find(t => t.tagName === tagName);
    return tag?.color || '#6b7280';
  };

  if (!inspection) return null;

  const formatTime = (time: string | undefined) => {
    if (!time) return null;
    const timeStr = time.toString().padStart(4, '0');
    const hours = parseInt(timeStr.substring(0, 2));
    const minutes = timeStr.substring(2, 4);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes} ${ampm}`;
  };

  const calculateDuration = (start: string | undefined, end: string | undefined) => {
    if (!start || !end) return null;
    const startMins = parseInt(start.toString().substring(0, 2)) * 60 + parseInt(start.toString().substring(2, 4));
    const endMins = parseInt(end.toString().substring(0, 2)) * 60 + parseInt(end.toString().substring(2, 4));
    const duration = endMins - startMins;
    return duration > 0 ? duration : null;
  };

  const startTime = inspection.inspStartTime || inspection.insp_start_time;
  const endTime = inspection.inspEndTime || inspection.insp_end_time;
  const formattedStart = formatTime(startTime);
  const formattedEnd = formatTime(endTime);
  const duration = calculateDuration(startTime, endTime);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}
      onClick={onClose}
    >
      <div
        className="card-modern"
        style={{
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-lg)', paddingBottom: 'var(--space-md)', borderBottom: '2px solid var(--color-gray-200)' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-900)', marginBottom: '0.75rem' }}>
              Inspection Details
            </h2>
            {/* Tags Section */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#9ca3af', marginRight: '4px' }}>TAGS:</span>
              {inspectionTags.map((tag) => (
                <InspectionTagBadge
                  key={tag}
                  tagName={tag}
                  color={getTagColor(tag)}
                  onRemove={() => handleRemoveTag(tag)}
                />
              ))}
              {showTagInput ? (
                <QuickTagInput
                  onTagAdd={(tagName) => {
                    handleAddTag(tagName);
                    setShowTagInput(false);
                  }}
                  onCancel={() => setShowTagInput(false)}
                  suggestedTags={availableShipperTags.map(t => t.tagName)}
                  autoFocus={true}
                />
              ) : (
                <button
                  onClick={() => setShowTagInput(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: 'white',
                    color: '#6b7280',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.color = '#3b82f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.color = '#6b7280';
                  }}
                >
                  <Tag size={12} />
                  + Tag
                </button>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--color-gray-500)',
              padding: '0.5rem',
              marginLeft: '1rem'
            }}
          >
            ×
          </button>
        </div>

        {/* Date & Time Section */}
        <div className="card-modern" style={{ marginBottom: 'var(--space-lg)' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-md)', color: 'var(--color-gray-900)' }}>
            📅 Date & Time
          </h3>
          <div className="grid-spacious" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <div className="stat-box">
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                {inspection.inspDate ? new Date(inspection.inspDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
              </div>
              <div className="stat-label">Date</div>
            </div>
            {formattedStart && (
              <div className="stat-box">
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                  {formattedStart}
                </div>
                <div className="stat-label">Start Time</div>
              </div>
            )}
            {formattedEnd && (
              <div className="stat-box">
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                  {formattedEnd}
                </div>
                <div className="stat-label">End Time</div>
              </div>
            )}
            {duration && (
              <div className="stat-box">
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                  {duration} min
                </div>
                <div className="stat-label">Duration</div>
              </div>
            )}
          </div>
        </div>

        {/* Location Section */}
        <div className="card-modern" style={{ marginBottom: 'var(--space-lg)' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-md)', color: 'var(--color-gray-900)' }}>
            📍 Location
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div className="stat-box">
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                {inspection.locationDesc || inspection.location_desc || 'N/A'}
              </div>
              <div className="stat-label">Location</div>
            </div>
            <div className="stat-box">
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                {inspection.reportState || inspection.state || 'N/A'}
              </div>
              <div className="stat-label">State</div>
            </div>
            <div className="stat-box">
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                {inspection.reportNumber || inspection.inspectionId || 'N/A'}
              </div>
              <div className="stat-label">Report Number</div>
            </div>
            <div className="stat-box">
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                Level {inspection.inspLevelId || 'N/A'}
              </div>
              <div className="stat-label">Inspection Level</div>
            </div>
            <div className="stat-box" style={{ backgroundColor: '#fef2f2', border: '2px solid #dc2626' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#dc2626', fontFamily: 'monospace' }}>
                {inspection.shippingPaperNumber || inspection.shipping_paper_number || 'NO DATA'}
              </div>
              <div className="stat-label" style={{ color: '#dc2626' }}>🚨 BOL# / Bill of Lading</div>
            </div>
          </div>
        </div>

        {/* Violation Breakdown */}
        <div className="card-modern" style={{ marginBottom: 'var(--space-lg)' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-md)', color: 'var(--color-gray-900)' }}>
            📊 Violation Breakdown
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
            <div className="stat-box" style={{ textAlign: 'center', padding: 'var(--space-md)' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: Number(inspection.violTotal) > 0 ? 'var(--color-danger)' : 'var(--color-success)' }}>
                {inspection.violTotal || 0}
              </div>
              <div className="stat-label">Total Violations</div>
            </div>
            <div className="stat-box" style={{ textAlign: 'center', padding: 'var(--space-md)' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: Number(inspection.oosTotal) > 0 ? 'var(--color-danger)' : 'var(--color-success)' }}>
                {inspection.oosTotal || 0}
              </div>
              <div className="stat-label">Out of Service</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-sm)' }}>
            <div style={{
              padding: 'var(--space-sm)',
              background: (inspection.driverViolTotal > 0 || inspection.driverOosTotal > 0) ? '#fee2e2' : '#f0fdf4',
              borderRadius: 'var(--radius-md)',
              border: `2px solid ${(inspection.driverViolTotal > 0 || inspection.driverOosTotal > 0) ? '#fca5a5' : '#86efac'}`
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-gray-600)', marginBottom: '4px' }}>👤 Driver</div>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                {inspection.driverViolTotal || 0} violations
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)' }}>
                ({inspection.driverOosTotal || 0} OOS)
              </div>
            </div>
            <div style={{
              padding: 'var(--space-sm)',
              background: (inspection.vehicleViolTotal > 0 || inspection.vehicleOosTotal > 0) ? '#fee2e2' : '#f0fdf4',
              borderRadius: 'var(--radius-md)',
              border: `2px solid ${(inspection.vehicleViolTotal > 0 || inspection.vehicleOosTotal > 0) ? '#fca5a5' : '#86efac'}`
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-gray-600)', marginBottom: '4px' }}>🚛 Vehicle</div>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                {inspection.vehicleViolTotal || 0} violations
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)' }}>
                ({inspection.vehicleOosTotal || 0} OOS)
              </div>
            </div>
            <div style={{
              padding: 'var(--space-sm)',
              background: (inspection.hmViolTotal > 0 || inspection.hazmatViolTotal > 0 || inspection.hazmatOosTotal > 0) ? '#fee2e2' : '#f0fdf4',
              borderRadius: 'var(--radius-md)',
              border: `2px solid ${(inspection.hmViolTotal > 0 || inspection.hazmatViolTotal > 0 || inspection.hazmatOosTotal > 0) ? '#fca5a5' : '#86efac'}`
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-gray-600)', marginBottom: '4px' }}>☢️ Hazmat</div>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                {inspection.hmViolTotal || inspection.hazmatViolTotal || 0} violations
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)' }}>
                ({inspection.hazmatOosTotal || 0} OOS)
              </div>
            </div>
          </div>
        </div>

        {/* Vehicle Details if available */}
        {((inspection.grossCombVehWt || inspection.gross_comb_veh_wt) || (inspection.inspInterstate || inspection.insp_interstate) || (inspection.postAccInd || inspection.post_acc_ind)) && (
          <div className="card-modern" style={{ marginBottom: 'var(--space-lg)' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-md)', color: 'var(--color-gray-900)' }}>
              🚛 Vehicle & Inspection Details
            </h3>
            <div className="grid-spacious" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              {(inspection.grossCombVehWt || inspection.gross_comb_veh_wt) && (
                <div className="stat-box">
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                    {(() => {
                      const weight = inspection.grossCombVehWt || inspection.gross_comb_veh_wt;
                      const tons = (weight / 2000).toFixed(1);
                      return `${weight.toLocaleString()} lbs (${tons} tons)`;
                    })()}
                  </div>
                  <div className="stat-label">Gross Vehicle Weight</div>
                </div>
              )}
              {(inspection.inspInterstate || inspection.insp_interstate) && (
                <div className="stat-box">
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                    {(inspection.inspInterstate || inspection.insp_interstate) === 'Y' ? (
                      <span className="badge-modern" style={{ background: '#dbeafe', color: '#1e40af', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                        Interstate
                      </span>
                    ) : 'Intrastate'}
                  </div>
                  <div className="stat-label">Interstate Status</div>
                </div>
              )}
              {(inspection.postAccInd || inspection.post_acc_ind) && (
                <div className="stat-box">
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                    {(inspection.postAccInd || inspection.post_acc_ind) === 'Y' ? (
                      <span className="badge-modern badge-danger" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                        Post-Accident
                      </span>
                    ) : 'Not Post-Accident'}
                  </div>
                  <div className="stat-label">Accident Status</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Carrier & Shipper */}
        {((inspection.inspCarrierName || inspection.insp_carrier_name) || (inspection.shipperName || inspection.shipper_name)) && (
          <div className="card-modern" style={{ marginBottom: 'var(--space-lg)' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-md)', color: 'var(--color-gray-900)' }}>
              🏢 Carrier & Shipper
            </h3>
            <div className="grid-spacious" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
              {(inspection.inspCarrierName || inspection.insp_carrier_name) && (
                <div className="stat-box">
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                    {inspection.inspCarrierName || inspection.insp_carrier_name}
                  </div>
                  <div className="stat-label">Carrier at Inspection</div>
                </div>
              )}
              {(inspection.shipperName || inspection.shipper_name) && (
                <div className="stat-box">
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                    {inspection.shipperName || inspection.shipper_name}
                  </div>
                  <div className="stat-label">Shipper</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Crash Details for Post-Accident Inspections */}
        {(inspection.postAccInd === 'Y' || inspection.post_acc_ind === 'Y') && inspection.crashId && (
          <div className="card-modern" style={{ marginBottom: 'var(--space-lg)', background: '#fef2f2', border: '2px solid #dc2626' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-md)', color: '#991b1b', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <AlertTriangle size={20} style={{ color: '#dc2626' }} />
              Related Crash Information
            </h3>
            <div style={{
              padding: 'var(--space-md)',
              background: 'white',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-md)'
            }}>
              <div style={{ fontSize: '0.875rem', color: '#991b1b', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                This post-accident inspection is linked to crash report: {inspection.crashReportNumber}
              </div>
              <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                Crash occurred on {inspection.crashDate ? new Date(inspection.crashDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown date'}
                {inspection.crashLocation && ` at ${inspection.crashLocation}`}
                {inspection.crashCity && ` in ${inspection.crashCity}`}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--space-md)' }}>
              {(inspection.fatalities !== undefined && inspection.fatalities !== null) && (
                <div className="stat-box" style={{ textAlign: 'center', background: inspection.fatalities > 0 ? '#fee2e2' : 'white' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: inspection.fatalities > 0 ? '#dc2626' : '#059669' }}>
                    {inspection.fatalities}
                  </div>
                  <div className="stat-label">Fatalities</div>
                </div>
              )}
              {(inspection.injuries !== undefined && inspection.injuries !== null) && (
                <div className="stat-box" style={{ textAlign: 'center', background: inspection.injuries > 0 ? '#fed7aa' : 'white' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: inspection.injuries > 0 ? '#ea580c' : '#059669' }}>
                    {inspection.injuries}
                  </div>
                  <div className="stat-label">Injuries</div>
                </div>
              )}
              {inspection.towAway && (
                <div className="stat-box" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                    {inspection.towAway === 'Y' ? (
                      <span style={{ color: '#dc2626' }}>Yes</span>
                    ) : 'No'}
                  </div>
                  <div className="stat-label">Tow Away</div>
                </div>
              )}
              {inspection.federalRecordable && (
                <div className="stat-box" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                    {inspection.federalRecordable === 'Y' ? (
                      <span style={{ color: '#dc2626' }}>Yes</span>
                    ) : 'No'}
                  </div>
                  <div className="stat-label">Federal Recordable</div>
                </div>
              )}
              {inspection.hazmatReleased && (
                <div className="stat-box" style={{ textAlign: 'center', background: inspection.hazmatReleased === 'Y' ? '#fee2e2' : 'white' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: inspection.hazmatReleased === 'Y' ? '#dc2626' : '#059669' }}>
                    {inspection.hazmatReleased === 'Y' ? 'Yes' : 'No'}
                  </div>
                  <div className="stat-label">Hazmat Released</div>
                </div>
              )}
            </div>
            {(inspection.firstHarmfulEvent || inspection.mostHarmfulEvent) && (
              <div style={{ marginTop: 'var(--space-md)', padding: 'var(--space-md)', background: 'white', borderRadius: 'var(--radius-md)' }}>
                {inspection.firstHarmfulEvent && (
                  <div style={{ marginBottom: 'var(--space-sm)' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-gray-600)' }}>First Harmful Event:</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-900)' }}>{inspection.firstHarmfulEvent}</div>
                  </div>
                )}
                {inspection.mostHarmfulEvent && (
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-gray-600)' }}>Most Harmful Event:</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-900)' }}>{inspection.mostHarmfulEvent}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Violations List */}
        {loadingViolations ? (
          <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>Loading detailed violations...</div>
          </div>
        ) : violations.length > 0 ? (
          <div className="card-modern">
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-md)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <AlertTriangle size={20} style={{ color: 'var(--color-danger)' }} />
              Detailed Violations ({violations.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              {violations.map((violation: any, idx: number) => (
                <div
                  key={idx}
                  style={{
                    padding: 'var(--space-md)',
                    background: violation.outOfServiceIndicator === 'Y' ? '#fee2e2' : '#f9fafb',
                    border: `2px solid ${violation.outOfServiceIndicator === 'Y' ? '#fca5a5' : '#e5e7eb'}`,
                    borderRadius: 'var(--radius-md)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 'var(--space-sm)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-gray-900)', fontFamily: 'monospace' }}>
                        {violation.violationCode || 'N/A'}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: '0.25rem' }}>
                        {violation.basicCategory || 'Unknown Category'}
                      </div>
                    </div>
                    {violation.outOfServiceIndicator === 'Y' && (
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        background: '#dc2626',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        borderRadius: '4px'
                      }}>
                        OOS
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-gray-700)' }}>
                    {violation.violationDescription || 'No description available'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !loadingViolations && (inspection.violTotal || 0) > 0 ? (
          <div className="card-modern" style={{ padding: 'var(--space-lg)', textAlign: 'center', background: '#fffbeb', border: '2px solid #fbbf24' }}>
            <AlertTriangle size={24} style={{ color: '#f59e0b', margin: '0 auto 0.5rem' }} />
            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#92400e', marginBottom: '0.5rem' }}>
              Detailed violations could not be loaded
            </div>
            <div style={{ fontSize: '0.8125rem', color: '#78350f' }}>
              This inspection has {inspection.violTotal} violation{inspection.violTotal !== 1 ? 's' : ''}.
              {inspection.dotNumber && (
                <span>
                  {' '}View full details on the{' '}
                  <a
                    href={`/carrier/${inspection.dotNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1e40af', textDecoration: 'underline', fontWeight: 600 }}
                  >
                    carrier profile page
                  </a>.
                </span>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
